import { useState } from 'react';

import {
  Alert,
  Button,
  Code,
  FileInput,
  Group,
  List,
  Loader,
  Modal,
  Stack,
  Text,
} from '@mantine/core';
import { IconAlertTriangle, IconFileImport } from '@tabler/icons-react';

import * as Sentry from '@sentry/react';

import { useReportConfigs } from '@/hooks';
import { ApiConflictError, ApiValidationError } from '@/services/vnext-api';
import {
  ConfigExportPayload,
  ImportMissingScopeComponentsDetail,
  ImportOnConflict,
  ImportSlugConflictDetail,
  ReportConfig,
} from '@/types';

interface ImportReportConfigModalProps {
  opened: boolean;
  onClose: () => void;
  /** Forwarded to the import call so admins can target a team. */
  teamId?: string | number;
  /** Called once the import succeeds. The page handles navigation. */
  onImported: (config: ReportConfig) => void;
}

type ImportError =
  | { kind: 'parse'; message: string }
  | { kind: 'missing_components'; slugs: string[]; message: string }
  | { kind: 'conflict'; slug: string; message: string }
  | { kind: 'generic'; message: string };

function parsePayload(text: string): ConfigExportPayload {
  const json: unknown = JSON.parse(text);
  if (
    !json ||
    typeof json !== 'object' ||
    (json as { format_version?: unknown }).format_version !== '1' ||
    !(json as { config?: unknown }).config
  ) {
    throw new Error('not a valid export');
  }
  return json as ConfigExportPayload;
}

export function ImportReportConfigModal({
  opened,
  onClose,
  teamId,
  onImported,
}: Readonly<ImportReportConfigModalProps>) {
  const { importConfig } = useReportConfigs({ teamId, autoFetch: false });

  const [file, setFile] = useState<File | null>(null);
  const [payload, setPayload] = useState<ConfigExportPayload | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<ImportError | null>(null);

  const reset = () => {
    setFile(null);
    setPayload(null);
    setSubmitting(false);
    setError(null);
  };

  const handleClose = () => {
    if (submitting) {
      return;
    }
    reset();
    onClose();
  };

  const handleFileChange = async (next: File | null) => {
    setError(null);
    setPayload(null);
    setFile(next);
    if (!next) {
      return;
    }
    try {
      const text = await next.text();
      const parsed = parsePayload(text);
      setPayload(parsed);
    } catch (err) {
      Sentry.captureException(err);
      setError({
        kind: 'parse',
        message:
          'This doesn\'t look like a valid ReportConfig export. Expected JSON with format_version "1".',
      });
    }
  };

  const runImport = async (onConflict: ImportOnConflict) => {
    if (!payload) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const created = await importConfig(payload, onConflict);
      reset();
      onImported(created);
    } catch (err) {
      if (err instanceof ApiConflictError) {
        const detail = err.detail as Partial<ImportSlugConflictDetail>;
        setError({
          kind: 'conflict',
          slug: detail.slug ?? payload.config.slug,
          message: detail.message ?? err.message,
        });
      } else if (err instanceof ApiValidationError) {
        const detail = err.detail as Partial<ImportMissingScopeComponentsDetail>;
        setError({
          kind: 'missing_components',
          slugs: detail.missing_scope_component_slugs ?? [],
          message: detail.message ?? err.message,
        });
      } else {
        const message = err instanceof Error ? err.message : 'Import failed';
        setError({ kind: 'generic', message });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const renderBody = () => {
    if (error?.kind === 'missing_components') {
      return (
        <Stack>
          <Alert color="orange" icon={<IconAlertTriangle size={16} />} title="Missing components">
            {error.message}
          </Alert>
          <Text size="sm">
            These ScopeComponents are not provisioned in the destination environment:
          </Text>
          <List spacing={4} size="sm">
            {error.slugs.map((slug) => (
              <List.Item key={slug}>
                <Code>{slug}</Code>
              </List.Item>
            ))}
          </List>
          <Text size="sm" c="dimmed">
            Provision the missing components in this environment, or pick a different target, then
            try again.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={handleClose}>
              Close
            </Button>
          </Group>
        </Stack>
      );
    }

    if (error?.kind === 'conflict' && payload) {
      return (
        <Stack>
          <Alert color="orange" icon={<IconAlertTriangle size={16} />} title="Slug already in use">
            A config with slug <Code>{error.slug}</Code> already exists in the target team.
          </Alert>
          <Text size="sm">Choose how to proceed:</Text>
          <List size="sm" spacing={4}>
            <List.Item>
              <Text span fw={600}>
                Rename
              </Text>{' '}
              — import as <Code>{error.slug}-2</Code> (or the next free suffix). Existing config is
              untouched.
            </List.Item>
            <List.Item>
              <Text span fw={600} c="red">
                Replace
              </Text>{' '}
              — soft-deletes the existing config and imports under the original slug.{' '}
              <Text span fw={700}>
                Destructive.
              </Text>
            </List.Item>
          </List>
          <Group justify="flex-end">
            <Button variant="default" onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={() => runImport('rename')}
              loading={submitting}
              disabled={submitting}
            >
              Rename
            </Button>
            <Button
              color="red"
              onClick={() => runImport('replace')}
              loading={submitting}
              disabled={submitting}
            >
              Replace
            </Button>
          </Group>
        </Stack>
      );
    }

    return (
      <Stack>
        <FileInput
          label="Export file"
          placeholder="Choose a .json file"
          accept="application/json,.json"
          value={file}
          onChange={handleFileChange}
          leftSection={<IconFileImport size={16} />}
          disabled={submitting}
          clearable
        />

        {error?.kind === 'parse' && (
          <Alert color="red" icon={<IconAlertTriangle size={16} />}>
            {error.message}
          </Alert>
        )}

        {error?.kind === 'generic' && (
          <Alert color="red" icon={<IconAlertTriangle size={16} />}>
            {error.message}
          </Alert>
        )}

        {payload && !error && (
          <Stack gap={4}>
            <Text size="sm">
              <Text span fw={600}>
                Name:
              </Text>{' '}
              {payload.config.name}
            </Text>
            <Text size="sm">
              <Text span fw={600}>
                Slug:
              </Text>{' '}
              <Code>{payload.config.slug}</Code>
            </Text>
            <Text size="sm" c="dimmed">
              {payload.config.components.length} component
              {payload.config.components.length === 1 ? '' : 's'}
            </Text>
          </Stack>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={() => runImport('error')}
            disabled={!payload || submitting}
            loading={submitting}
            leftSection={!submitting && <IconFileImport size={16} />}
          >
            Import
          </Button>
        </Group>

        {submitting && (
          <Group justify="center">
            <Loader size="sm" />
            <Text size="sm" c="dimmed">
              Importing…
            </Text>
          </Group>
        )}
      </Stack>
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Import Report Configuration"
      size="md"
      closeOnClickOutside={!submitting}
      closeOnEscape={!submitting}
      withCloseButton={!submitting}
    >
      {renderBody()}
    </Modal>
  );
}
